import { BrandColorsThemeOption } from "@generated-types"
import { brandVariants } from "@utils/brand-variants"
import { cx, tv, VariantProps } from "@utils/tw"
import { AnimatePresence, motion, MotionProps } from "motion/react"
import { Children, ComponentProps, forwardRef, ReactNode } from "react"

type BaseProps = {
  theme: BrandColorsThemeOption
  media: ReactNode
  title: ReactNode
  tags?: ReactNode
  description?: ReactNode
  widget?: ReactNode
  className?: string
  animationKey?: string
} & VariantProps<typeof createStyles>

type Props = BaseProps & Omit<ComponentProps<"div">, keyof BaseProps>

const fadeVariants: MotionProps = {
  variants: {
    hide: { opacity: 0 },
    show: { opacity: 1 },
  },
}

export const LargeCard = forwardRef<HTMLDivElement, Props>(
  (
    { theme, media, title, tags, description, className, layout, infoWidth, height, widget, animationKey, ...props },
    ref,
  ) => {
    const styles = createStyles({ layout, infoWidth, height })
    return (
      <div {...props} className={styles.base({ className })} ref={ref}>
        <div className={cx(styles.info(), brandVariants({ theme }))}>
          {widget && <div className={styles.infoWidget()}>{widget}</div>}
          <AnimatePresence mode="wait">
            <motion.div
              initial="hide"
              animate="show"
              exit="hide"
              key={animationKey}
              className={styles.infoInner()}
              transition={{ staggerChildren: 0.05 }}
            >
              <motion.div className={styles.title()} {...fadeVariants}>
                {title}
              </motion.div>
              <div className={styles.infoSpacer()} />
              {tags && (
                <div className={styles.tags()}>
                  {Children.toArray(tags).map((child, i) => {
                    return (
                      <motion.div key={i} {...fadeVariants} className="inline-flex">
                        {child}
                      </motion.div>
                    )
                  })}
                </div>
              )}
              {description && (
                <motion.div className={styles.description()} {...fadeVariants}>
                  {description}
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={animationKey}
            className={styles.media()}
            initial="hide"
            animate="show"
            exit="hide"
            variants={{
              hide: { opacity: 0 },
              show: { opacity: 1 },
            }}
          >
            {media}
          </motion.div>
        </AnimatePresence>
      </div>
    )
  },
)

const createStyles = tv({
  base: "",
  slots: {
    info: "p-5 md:p-6 order-1 flex flex-col transition-colors duration-100",
    infoInner: "flex flex-col flex-1",
    infoSpacer: "flex-1 min-h-6",
    title: "type-title-xl text-balance",
    description: "mt-4 type-body-m text-pretty",
    tags: "flex flex-wrap gap-3 md:gap-2",
    media: "relative flex-1",
    infoWidget: "mb-3",
  },
  variants: {
    layout: {
      auto: {
        base: ["flex flex-col md:flex-row"],
        info: ["w-full md:-order-1"],
      },
      stacked: {
        base: ["flex flex-col"],
        info: ["!w-full"],
      },
      row: {
        base: ["flex flex-row"],
        info: ["w-full -order-1"],
      },
    },
    infoWidth: {
      video: {
        base: "!min-h-1",
        info: ["md:flex-1"],
        media: ["h-full md:w-cols-7 aspect-video flex-none"],
        infoSpacer: ["md:min-h-2"],
      },
      sm: {
        info: ["md:w-cols-6"],
      },
      md: {
        info: ["md:w-cols-5"],
      },
      lg: {
        info: ["md:w-cols-4"],
      },
    },
    height: {
      sm: {
        base: ["md:min-h-[20vw] xl:min-h-[calc(var(--spacing-8)_*_6)]"],
      },
      md: {
        base: ["md:min-h-[25vw] xl:min-h-[calc(var(--spacing-8)_*_8)]"],
      },
      lg: {
        base: ["md:min-h-[30vw] xl:min-h-[calc(var(--spacing-8)_*_9)]"],
      },
    },
  },
  defaultVariants: {
    mediaWidth: "md",
    layout: "auto",
    infoWidth: "sm",
    height: "sm",
  },
})
